import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { getSettings, saveSettings, setImpliedSuggestions } from "../lib/api";
import { DOMAIN_OPTIONS, DOMAIN_META } from "../lib/meta";
import { Loader2, ShieldCheck, ChevronRight } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { SettingsSkeleton } from "../components/Skeleton";
import { WhatsAppCard } from "../components/WhatsAppCard";
import { DeliveryCard } from "../components/DeliveryCard";
import { RecentlyDeletedCard } from "../components/RecentlyDeletedCard";
import { SettingsLinksCard } from "../components/SettingsLinksCard";
import TrustPrivacySheet from "../components/TrustPrivacySheet";

function Toggle({ checked, onChange, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={() => onChange(!checked)}
      className={`relative h-7 w-12 rounded-full transition-colors duration-200 ${
        checked ? "bg-[var(--pine)]" : "bg-[var(--pine-line)]"
      }`}
    >
      <span
        className={`absolute top-1 left-1 h-5 w-5 rounded-full bg-[var(--surface)] shadow transition-transform duration-200 ${
          checked ? "translate-x-5" : ""
        }`}
      />
    </button>
  );
}

function Row({ title, desc, children }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-6 py-5 border-b border-[var(--line2)] last:border-0">
      <div className="sm:max-w-md">
        <p className="text-[var(--ink)] font-medium">{title}</p>
        <p className="text-sm text-[var(--ink3)] mt-0.5">{desc}</p>
      </div>
      <div className="shrink-0 sm:pt-1">{children}</div>
    </div>
  );
}

const REMINDER_MODES = [
  { value: "later_today", label: "Later today" },
  { value: "tomorrow", label: "Tomorrow" },
  { value: "this_week", label: "This week" },
];

// Calm, household-facing domains. "Prospect" is a sales/work concept kept under Advanced.
const HOUSEHOLD_DOMAINS = DOMAIN_OPTIONS.filter((d) => d !== "PROSPECT");

function ImpliedSuggestionsRow() {
  const { user, refresh } = useAuth();
  const [enabled, setEnabled] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user && typeof user.implied_suggestions_enabled === "boolean") {
      setEnabled(user.implied_suggestions_enabled);
    }
  }, [user]);

  const toggle = (v) => {
    setEnabled(v); // optimistic
    setSaving(true);
    setImpliedSuggestions(v)
      .then(() => refresh && refresh())
      .catch(() => {
        setEnabled(!v);
        toast.error("Couldn't update that preference.");
      })
      .finally(() => setSaving(false));
  };

  if (!user) return null;

  return (
    <>
      <Row
        title={'"I noticed" suggestions'}
        desc="When you capture something that might involve obvious prep, like a school start, a birthday or a trip, Heldall can quietly surface optional suggestions."
      >
        <Toggle
          testid="toggle-implied-suggestions"
          checked={enabled}
          onChange={(v) => !saving && toggle(v)}
        />
      </Row>
      {!enabled && (
        <p className="text-sm text-[var(--ink3)] pb-5 -mt-3" data-testid="implied-off-note">
          Heldall will still catch everything you say. It just won't suggest anything extra.
        </p>
      )}
    </>
  );
}

export default function Settings() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);

  useEffect(() => { getSettings().then(setSettings); }, []);

  const update = (patch) => setSettings((s) => ({ ...s, ...patch }));

  const save = () => {
    setSaving(true);
    // read the freshest state via the updater to avoid stale-closure races
    setSettings((latest) => {
      saveSettings(latest)
        .then((saved) => {
          setSettings(saved);
          toast.success("Settings saved.");
        })
        .catch(() => toast.error("Couldn't save settings."))
        .finally(() => setSaving(false));
      return latest;
    });
  };

  if (!settings) {
    return (
      <div className="max-w-2xl mx-auto px-6 md:px-10 py-10 md:py-14">
        <div className="mb-8">
          <h1 className="font-display text-4xl tracking-tight font-medium text-[var(--ink)]">Settings</h1>
          <p className="text-[var(--ink3)] mt-2">A few quiet preferences. Nothing fiddly.</p>
        </div>
        <SettingsSkeleton />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-6 md:px-10 py-10 md:py-14">
      <div className="mb-8">
        <h1 className="font-display text-4xl tracking-tight font-medium text-[var(--ink)]">Settings</h1>
        <p className="text-[var(--ink3)] mt-2">A few quiet preferences. Nothing fiddly.</p>
      </div>

      <DeliveryCard />

      <WhatsAppCard />

      <RecentlyDeletedCard />

      <div className="rounded-2xl bg-[var(--surface)] border border-[var(--line)] shadow-[0_2px_8px_rgba(0,0,0,0.04)] px-6 md:px-8 pt-6 pb-2" data-testid="settings-preferences-card">
        <h2 className="font-serif-display text-lg text-[var(--ink)] mb-1">How Heldall works for you</h2>
        <p className="text-sm text-[var(--ink3)] mb-3">A few quiet choices about how Heldall reads, reminds, and sorts what you say.</p>

        <ImpliedSuggestionsRow />

        <Row
          title="Default reminder timing"
          desc="When no time is mentioned, suggest this by default."
        >
          <div className="flex sm:justify-end">
            <div className="inline-flex items-center gap-1 rounded-full bg-[var(--line2)] p-1">
              {REMINDER_MODES.map((m) => (
                <button
                  key={m.value}
                  data-testid={`reminder-mode-${m.value}`}
                  onClick={() => update({ default_reminder_mode: m.value })}
                  className={`rounded-full px-3.5 py-1.5 text-sm font-semibold transition-all duration-200 ${
                    settings.default_reminder_mode === m.value
                      ? "bg-[var(--surface)] text-[var(--ink)] shadow-[0_2px_8px_rgba(0,0,0,0.06)]"
                      : "text-[var(--ink3)] hover:text-[var(--ink2)]"
                  }`}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>
        </Row>

        <Row
          title="Time suggestions"
          desc="Heldall can suggest when something should come back. Turn off to use internal reminders only."
        >
          <Toggle
            testid="toggle-calendar"
            checked={settings.calendar_suggestions_enabled}
            onChange={(v) => update({ calendar_suggestions_enabled: v })}
          />
        </Row>

        <Row
          title="Default category"
          desc="Used when the category isn't obvious from what you said."
        >
          <div className="flex gap-1.5 flex-wrap sm:justify-end sm:max-w-[260px]">
            {HOUSEHOLD_DOMAINS.map((d) => (
              <button
                key={d}
                data-testid={`default-domain-${d}`}
                onClick={() => update({ default_domain: d })}
                className={`rounded-full px-3 py-1 text-xs font-semibold border transition-all duration-200 ${
                  settings.default_domain === d
                    ? "ring-2 ring-[var(--pine)] " + DOMAIN_META[d].cls
                    : DOMAIN_META[d].cls + " opacity-70 hover:opacity-100"
                }`}
              >
                {DOMAIN_META[d].label}
              </button>
            ))}
          </div>
        </Row>
      </div>

      <div className="mt-6 flex flex-wrap items-center justify-end gap-x-3 gap-y-2">
        <p className="text-xs text-[var(--ink5)] mr-auto" data-testid="settings-save-hint">
          Everywhere else saves on its own. These preferences apply when you save.
        </p>
        <button
          data-testid="save-settings-btn"
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-2 rounded-full bg-[var(--pine)] hover:bg-[var(--pine-dark)] text-[var(--on-pine)] font-semibold px-6 py-3 text-sm shadow-[0_8px_24px_rgba(44,90,77,0.25)] transition-all duration-200 disabled:opacity-50"
        >
          {saving && <Loader2 size={16} className="animate-spin" />}
          Save settings
        </button>
      </div>

      <div className="mt-8">
        <div className="rounded-2xl bg-[var(--surface)] border border-[var(--line)] shadow-[0_2px_8px_rgba(0,0,0,0.04)] p-6 md:p-7 mb-6" data-testid="data-privacy-card">
          <div className="flex items-center gap-2 mb-4">
            <ShieldCheck size={16} className="text-[var(--pine)]" strokeWidth={1.9} />
            <h2 className="text-sm uppercase tracking-[0.18em] font-bold text-[var(--ink3)]">Privacy &amp; security</h2>
          </div>
          <div className="text-sm text-[var(--ink3)] space-y-2 mb-5">
            <p>Your private items stay private. Only you can see them.</p>
            <p>Household items can be visible to the people in your household, depending on how they're shared or handed over.</p>
            <p>Heldall does not do partner surveillance. There are no read receipts, no opened-at tracking, and no seen-by-your-partner timestamps.</p>
            <p>You can export your data or close your account any time from Account.</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Link
              to="/account"
              data-testid="manage-account-data-link"
              className="inline-flex items-center gap-2 rounded-full border border-[var(--line)] bg-[var(--surface)] px-4 py-2.5 text-sm font-semibold text-[var(--ink2)] hover:border-[var(--pine)] hover:text-[var(--pine)] transition-all duration-200"
            >
              Manage account data
              <ChevronRight size={16} strokeWidth={2} />
            </Link>
            <button
              type="button"
              data-testid="privacy-summary-btn"
              onClick={() => setPrivacyOpen(true)}
              className="inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold text-[var(--ink3)] hover:text-[var(--pine)] transition-colors duration-200"
            >
              Privacy summary
            </button>
          </div>
        </div>
        <SettingsLinksCard />
      </div>

      <TrustPrivacySheet open={privacyOpen} onClose={() => setPrivacyOpen(false)} />
    </div>
  );
}
